/**
 * User API Contract
 *
 * oRPC contract for user management endpoints.
 * Admin-only endpoints for user listing, banning, etc.
 */

import { oc } from "@orpc/contract";
import { z } from "zod";

// ============================================
// Zod Schemas
// ============================================

export const UserSchema = z.object({
  id: z.string().uuid(),
  email: z.string().email().nullable(),
  name: z.string().nullable(),
  emailVerified: z.boolean(),
  role: z.string().nullable(),
  banned: z.boolean().nullable(),
  banReason: z.string().nullable(),
  createdAt: z.coerce.date(),
});
export type User = z.infer<typeof UserSchema>;

export const ListUsersRequestSchema = z.object({
  limit: z.coerce.number().int().min(1).max(100).optional().default(20),
  offset: z.coerce.number().int().min(0).optional().default(0),
});
export type ListUsersRequest = z.infer<typeof ListUsersRequestSchema>;

export const ListUsersResponseSchema = z.object({
  users: z.array(UserSchema),
  total: z.number().int().nonnegative(),
  limit: z.number().int().positive(),
  offset: z.number().int().nonnegative(),
});
export type ListUsersResponse = z.infer<typeof ListUsersResponseSchema>;

export const GetUserResponseSchema = z.object({
  user: UserSchema,
});
export type GetUserResponse = z.infer<typeof GetUserResponseSchema>;

export const BanUserRequestSchema = z.object({
  id: z.string().uuid(),
  reason: z.string().min(1),
});
export type BanUserRequest = z.infer<typeof BanUserRequestSchema>;

export const UnbanUserRequestSchema = z.object({
  id: z.string().uuid(),
});
export type UnbanUserRequest = z.infer<typeof UnbanUserRequestSchema>;

// ============================================
// oRPC Contract Definition
// ============================================

/**
 * User management oRPC contract.
 * All endpoints require admin authentication.
 */
export const userContract = {
  /**
   * List all users with pagination.
   */
  list: oc
    .route({ method: "GET", path: "/users" })
    .input(ListUsersRequestSchema)
    .output(ListUsersResponseSchema),

  /**
   * Get a specific user by ID.
   */
  get: oc
    .route({ method: "GET", path: "/users/{id}" })
    .input(z.object({ id: z.string().uuid() }))
    .output(GetUserResponseSchema),

  /**
   * Ban a user with a reason.
   */
  ban: oc
    .route({ method: "POST", path: "/users/{id}/ban" })
    .input(BanUserRequestSchema)
    .output(GetUserResponseSchema),

  /**
   * Unban a user.
   */
  unban: oc
    .route({ method: "POST", path: "/users/{id}/unban" })
    .input(UnbanUserRequestSchema)
    .output(GetUserResponseSchema),
};
